import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LifeBuoy, Plus, MessageSquare, Clock, AlertCircle, CheckCircle, X, Send } from 'lucide-react';
import { useAppContext, Ticket } from '../context/AppContext';
interface SupportTicketPanelProps {
  userType: 'customer' | 'business';
  userName: string;
  userEmail: string;
}
const departments = ['Technical Support', 'Billing & Accounts', 'Sales', 'General Inquiry'];
export function SupportTicketPanel({
  userType,
  userName,
  userEmail
}: SupportTicketPanelProps) {
  const {
    tickets,
    addTicket
  } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Filter tickets for this user
  const userTickets = tickets.filter(t => t.clientEmail === userEmail);
  const handleCreateTicket = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const department = formData.get('department') as string;
    const subject = formData.get('subject') as string;
    const priority = formData.get('priority') as Ticket['priority'];
    const message = formData.get('message') as string;
    const newTicket: Ticket = {
      id: `TKT-${Math.floor(1000 + Math.random() * 9000)}`,
      subject: `[${department}] ${subject}`,
      clientName: userName,
      clientEmail: userEmail,
      priority,
      status: 'open',
      messages: [{
        id: `m_${Date.now()}`,
        sender: userName,
        role: 'client',
        content: message,
        createdAt: new Date().toISOString()
      }],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    addTicket(newTicket);
    setIsModalOpen(false);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'in-progress':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'resolved':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'closed':
        return 'bg-slate-800 text-slate-400 border-slate-700';
      default:
        return 'bg-slate-800 text-slate-400 border-slate-700';
    }
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
      case 'critical':
        return 'text-red-500';
      case 'medium':
        return 'text-amber-500';
      case 'low':
        return 'text-emerald-500';
      default:
        return 'text-slate-400';
    }
  };
  return <div className="space-y-8" data-id="element-1335">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1336">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-1337">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1338">
            Support Center
          </h1>
          <p className="text-slate-400" data-id="element-1339">
            Manage your support tickets and get help from our team.
          </p>
        </motion.div>
        <button onClick={() => setIsModalOpen(true)} className="bg-fox-600 hover:bg-fox-500 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors shadow-lg shadow-fox-500/20" data-id="element-1340">
          <Plus size={18} data-id="element-1341" /> Create Ticket
        </button>
      </div>

      {/* Tickets List */}
      <div className="space-y-4" data-id="element-1342">
        {userTickets.length === 0 ? <div className="bg-slate-950 border border-slate-800 rounded-2xl p-12 text-center" data-id="element-1343">
            <LifeBuoy size={48} className="mx-auto text-slate-700 mb-4" data-id="element-1344" />
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-1345">
              No Support Tickets
            </h3>
            <p className="text-slate-500 mb-6" data-id="element-1346">
              You don't have any open or past support tickets.
            </p>
            <button onClick={() => setIsModalOpen(true)} className="bg-slate-800 hover:bg-slate-700 text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-1347">
              Open a Ticket
            </button>
          </div> : userTickets.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).map((ticket, i) => <motion.div key={ticket.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6 hover:border-slate-700 transition-colors cursor-pointer group" data-id="element-1348">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1349">
                  <div className="flex-1" data-id="element-1350">
                    <div className="flex items-center gap-3 mb-2" data-id="element-1351">
                      <span className="text-sm font-mono text-slate-500" data-id="element-1352">
                        {ticket.id}
                      </span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`} data-id="element-1353">
                        {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                      </span>
                      <span className={`text-xs font-medium flex items-center gap-1 ${getPriorityColor(ticket.priority)}`} data-id="element-1354">
                        <AlertCircle size={12} data-id="element-1355" />
                        {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}{' '}
                        Priority
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white group-hover:text-fox-500 transition-colors" data-id="element-1356">
                      {ticket.subject}
                    </h3>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-slate-400" data-id="element-1357">
                    <div className="flex items-center gap-2" data-id="element-1358">
                      <MessageSquare size={16} data-id="element-1359" />
                      <span data-id="element-1360">{ticket.messages.length} messages</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1361">
                      <Clock size={16} data-id="element-1362" />
                      <span data-id="element-1363">
                        Updated{' '}
                        {new Date(ticket.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>)}
      </div>

      {/* Create Ticket Modal */}
      <AnimatePresence data-id="element-1364">
        {isModalOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1365">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-1366">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-1367">
                <h2 className="text-xl font-bold text-white flex items-center gap-2" data-id="element-1368">
                  <LifeBuoy size={20} className="text-fox-500" data-id="element-1369" />
                  Create Support Ticket
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-1370">
                  <X size={24} data-id="element-1371" />
                </button>
              </div>

              <form onSubmit={handleCreateTicket} className="p-6 space-y-6" data-id="element-1372">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1373">
                  <div className="space-y-2" data-id="element-1374">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1375">
                      Department *
                    </label>
                    <select name="department" required className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-1376">
                      {departments.map(dept => <option key={dept} value={dept} data-id="element-1377">
                          {dept}
                        </option>)}
                    </select>
                  </div>

                  <div className="space-y-2" data-id="element-1378">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1379">
                      Priority Level *
                    </label>
                    <select name="priority" required defaultValue="medium" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-1380">
                      <option value="low" data-id="element-1381">Low - General Question</option>
                      <option value="medium" data-id="element-1382">Medium - Issue / Bug</option>
                      <option value="high" data-id="element-1383">High - Service Down / Urgent</option>
                    </select>
                  </div>

                  <div className="space-y-2 md:col-span-2" data-id="element-1384">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1385">
                      Subject *
                    </label>
                    <input name="subject" required placeholder="Brief description of the issue" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-1386" />
                  </div>

                  <div className="space-y-2 md:col-span-2" data-id="element-1387">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1388">
                      Message Details *
                    </label>
                    <textarea name="message" required rows={6} placeholder="Please provide as much detail as possible so we can help you quickly..." className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none resize-none" data-id="element-1389" />
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-1390">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors" data-id="element-1391">
                    Cancel
                  </button>
                  <button type="submit" className="bg-fox-600 hover:bg-fox-500 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2" data-id="element-1392">
                    <Send size={16} data-id="element-1393" /> Submit Ticket
                  </button>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}